import { getApiUrl, getApimSubscriptionKey } from './runtime-config';

/** Standard APIM subscription header. Also accepted by APIM as a query param; we never use that. */
export const APIM_SUBSCRIPTION_HEADER = 'Ocp-Apim-Subscription-Key';

/** Re-mint this many ms before the token actually expires, to avoid a race at the edge. */
const EXPIRY_SKEW_MS = 30_000;

interface TokenState {
  token: string;
  /** Epoch ms when the token expires. */
  expiresAt: number;
}

/**
 * The bearer token lives in memory only (never localStorage), so it dies with the tab and
 * cannot be read back by a later page load. It is fetched from POST /api/auth/token.
 */
let tokenState: TokenState | null = null;
/** Set once the backend reports auth is disabled, so we stop trying to mint. */
let authDisabled = false;
/** Coalesces concurrent mint calls so a burst of requests triggers a single token fetch. */
let inflightMint: Promise<void> | null = null;

/** Join runtime base URL with a path that starts with `/api/...`. */
export function apiUrl(path: string): string {
  const base = getApiUrl();
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/** APIM headers only (subscription key). Used for the token-mint call to avoid recursion. */
function apimHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  const key = getApimSubscriptionKey();
  if (key) {
    headers.set(APIM_SUBSCRIPTION_HEADER, key);
  }
  return headers;
}

/**
 * Fetch a fresh access token. Presents the current (possibly expired) token so the backend
 * can continue the same session under its absolute cap; on the first call there is none.
 */
async function mintToken(): Promise<void> {
  const headers = apimHeaders({ 'Content-Type': 'application/json' });
  if (tokenState?.token) {
    headers.set('Authorization', `Bearer ${tokenState.token}`);
  }
  // credentials: 'omit' so the browser never attaches ambient cookies (e.g. an
  // embedding app's Auth.js session cookie) to our API. Auth is via the bearer header.
  const response = await fetch(apiUrl('/api/auth/token'), {
    method: 'POST',
    headers,
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`Token request failed: HTTP ${response.status}`);
  }
  const data = await response.json().catch(() => null);
  // Backend explicitly signals auth is off - stop minting and send no bearer.
  if (data && data.enabled === false) {
    authDisabled = true;
    tokenState = null;
    return;
  }
  if (!data || typeof data.access_token !== 'string') {
    throw new Error('Token response missing access_token');
  }
  const expiresInSec = typeof data.expires_in === 'number' ? data.expires_in : 900;
  tokenState = { token: data.access_token, expiresAt: Date.now() + expiresInSec * 1000 };
}

/**
 * Ensure a usable token exists. Pass `force` after a 401 to re-mint even if the cached
 * token still looks valid. Concurrent callers share one in-flight mint.
 */
async function ensureToken(force = false): Promise<void> {
  if (authDisabled) return;
  const fresh = tokenState !== null && Date.now() < tokenState.expiresAt - EXPIRY_SKEW_MS;
  if (!force && fresh) return;
  if (!inflightMint) {
    inflightMint = mintToken().finally(() => {
      inflightMint = null;
    });
  }
  await inflightMint;
}

/**
 * Headers for every backend call: the APIM subscription key plus the bearer token when we
 * have one. CORS on APIM/backend must allow both the subscription and Authorization headers.
 */
export function apiHeaders(init?: HeadersInit): Headers {
  const headers = apimHeaders(init);
  if (tokenState?.token) {
    headers.set('Authorization', `Bearer ${tokenState.token}`);
  }
  return headers;
}

/**
 * fetch() that targets APIM (or same-origin / FastAPI), attaches APIM + bearer headers,
 * and silently re-mints once on a 401 (expired/rotated token) before retrying.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // A mint failure here shouldn't block the attempt; a genuinely-required token will
  // surface as a 401 and trigger the retry path below.
  try {
    await ensureToken();
  } catch {
    /* proceed; may 401 then retry */
  }

  // credentials: 'omit' - our API is authenticated by the bearer header only, so we never
  // want the browser attaching ambient cookies (e.g. an embedding app's session cookie).
  let response = await fetch(apiUrl(path), {
    ...init,
    headers: apiHeaders(init.headers),
    credentials: 'omit',
  });

  if (response.status === 401 && !authDisabled) {
    try {
      await ensureToken(true);
    } catch {
      /* fall through; the retry may still 401, which we return to the caller */
    }
    response = await fetch(apiUrl(path), {
      ...init,
      headers: apiHeaders(init.headers),
      credentials: 'omit',
    });
  }

  return response;
}

/** Test-only: clear the cached token and auth flags so each test starts clean. */
export function resetAuthForTests(): void {
  tokenState = null;
  authDisabled = false;
  inflightMint = null;
}

/** Test-only: inject a token so apiFetch skips the mint step. */
export function setAccessTokenForTests(token: string, expiresInSec = 900): void {
  tokenState = { token, expiresAt: Date.now() + expiresInSec * 1000 };
}
